import { useRef, useCallback } from "react";

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface CacheKey {
  type:
    | "accountTypes"
    | "accounts"
    | "contacts"
    | "vendors"
    | "customers"
    | "invoices"
    | "vendor_bills"
    | "transferAccounts"
    | "depositAccounts"
    | "glMappings"
    | "accountTree"
    | "bankGlTree";
  params: string; // JSON stringified params for cache key
}

// Cache TTL: 5 minutes (adjust as needed)
const CACHE_TTL = 10 * 60 * 1000;

interface UseDropdownCacheReturn {
  getCached: <T>(type: CacheKey["type"], params: any) => T | null;
  setCached: <T>(type: CacheKey["type"], params: any, data: T) => void;
  clearCache: (type?: CacheKey["type"]) => void;
}

export function useDropdownCache(): UseDropdownCacheReturn {
  const cacheRef = useRef<Map<string, CacheEntry<any>>>(new Map());

  const getCacheKey = useCallback(
    (type: CacheKey["type"], params: any): string => {
      return `${type}:${JSON.stringify(params)}`;
    },
    [],
  );

  const getCached = useCallback(
    <T>(type: CacheKey["type"], params: any): T | null => {
      const key = getCacheKey(type, params);
      const entry = cacheRef.current.get(key);

      if (!entry) return null;

      // Check if cache is still valid
      const now = Date.now();
      if (now - entry.timestamp > CACHE_TTL) {
        cacheRef.current.delete(key);
        return null;
      }

      return entry.data as T;
    },
    [getCacheKey],
  );

  const setCached = useCallback(
    <T>(type: CacheKey["type"], params: any, data: T): void => {
      const key = getCacheKey(type, params);
      cacheRef.current.set(key, {
        data,
        timestamp: Date.now(),
      });
    },
    [getCacheKey],
  );

  const clearCache = useCallback((type?: CacheKey["type"]): void => {
    if (type) {
      // Clear specific type
      const keysToDelete: string[] = [];
      cacheRef.current.forEach((_, key) => {
        if (key.startsWith(`${type}:`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach((key) => cacheRef.current.delete(key));
    } else {
      // Clear all
      cacheRef.current.clear();
    }
  }, []);

  return { getCached, setCached, clearCache };
}
